import { ReactNode, useCallback, useMemo } from 'react'
import clsx from 'clsx'
import {
  Button,
  ColumnsType,
  Icon,
  Link,
  Popover,
  Spin,
  Table,
  Text,
} from 'components/common'
import {
  BookingStatusOfTheDayQuery,
  BookingStatusOfTheDayProductVariants,
  OrderQuery,
  OrderPurpose,
} from 'lib/graphql/storeadmin'
import { OrderForObserverQuery } from 'lib/graphql/observer'

import { routes } from 'config'
import { date as dateFormat, time as timeFormat } from 'lib/str-formatter'

export interface BookingStatusTableProps {
  data?: BookingStatusOfTheDayQuery
  orderData?: OrderQuery | OrderForObserverQuery
  orderLoading?: boolean
  getOrderData?: (value: string) => void
  loading: boolean
}

interface PopoverLineProps {
  title: string
  content: string | ReactNode
}

const timeArray = new Array(24).fill(null).map((_, index) => {
  if (index < 10) {
    return `0${index}:00`
  } else {
    return `${index}:00`
  }
})

const checkPurpose = (purpose: string) => {
  switch (purpose) {
    case OrderPurpose.General:
      return '一般利用'
    case OrderPurpose.Event:
      return 'イベント利用'
    default:
      return ''
  }
}

const PopoverLineItem = ({ title, content }: PopoverLineProps) => (
  <div className={clsx('flex', 'justify-between', 'items-center')}>
    <div className={clsx('flex-initial')}>
      <Text>{title}</Text>
    </div>

    <div className={clsx('flex', 'flex-auto', 'justify-end')}>{content}</div>
  </div>
)

// observerではcustomer情報を取得させてはいけない為に分岐処理させています
const isOrderQuery = (
  data: OrderQuery | OrderForObserverQuery
): data is OrderQuery => {
  return (data as OrderQuery).order !== undefined
}
const isOrderForObserverQuery = (
  data: OrderQuery | OrderForObserverQuery
): data is OrderForObserverQuery => {
  return (data as OrderForObserverQuery).orderForObserver !== undefined
}

export const BookingStatusTable = ({
  data,
  orderData,
  getOrderData,
  orderLoading,
  loading,
}: BookingStatusTableProps) => {
  const popoverContent = useMemo(() => {
    if (orderLoading) {
      return <Spin />
    }

    if (orderData) {
      if (isOrderQuery(orderData)) {
        const {
          id,
          bookingDates,
          customer,
          numberOfPeople,
          orderNumber,
          period,
          purpose,
        } = orderData.order
        return (
          <div style={{ width: '300px' }}>
            {orderNumber && (
              <PopoverLineItem
                title="予約番号"
                content={
                  <Link
                    href={`${routes.bookings}/${id}`}
                    target="_blank"
                    rel="noopener noreferer"
                  >
                    #{orderNumber.toString()}
                  </Link>
                }
              />
            )}
            {customer && (
              <>
                <PopoverLineItem
                  title="お客様"
                  content={
                    <Link
                      href={`/admin/customers/${customer?.type?.toLowerCase()}/${
                        customer?.id
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {customer?.lastName || ''} {customer?.firstName || ''}
                    </Link>
                  }
                />
                {customer?.corporationName && (
                  <PopoverLineItem
                    title="団体名"
                    content={customer.corporationName}
                  />
                )}
              </>
            )}
            {bookingDates && (
              <PopoverLineItem
                title="利用予定日"
                content={`${dateFormat(bookingDates[0]?.date)} - 
              ${dateFormat(bookingDates[bookingDates?.length - 1]?.date)}`}
              />
            )}
            {period && (
              <PopoverLineItem
                title={'利用予定時間'}
                content={`${timeFormat(period?.start)} - ${timeFormat(
                  period?.end
                )}`}
              />
            )}
            {numberOfPeople && (
              <PopoverLineItem
                title={'利用予定人数'}
                content={<div>{numberOfPeople.toString()} 人</div>}
              />
            )}
            {purpose && (
              <PopoverLineItem
                title={'利用目的'}
                content={checkPurpose(purpose)}
              />
            )}
          </div>
        )
      } else if (isOrderForObserverQuery(orderData)) {
        const { bookingDates, numberOfPeople, period, purpose } =
          orderData.orderForObserver
        return (
          <div style={{ width: '300px' }}>
            {bookingDates && (
              <PopoverLineItem
                title="利用予定日"
                content={`${dateFormat(bookingDates[0]?.date)} - 
              ${dateFormat(bookingDates[bookingDates?.length - 1]?.date)}`}
              />
            )}
            {period && (
              <PopoverLineItem
                title={'利用予定時間'}
                content={`${timeFormat(period?.start)} - ${timeFormat(
                  period?.end
                )}`}
              />
            )}
            {numberOfPeople && (
              <PopoverLineItem
                title={'利用予定人数'}
                content={<div>{numberOfPeople.toString()} 人</div>}
              />
            )}
            {purpose && <PopoverLineItem title={'区分'} content={purpose} />}
          </div>
        )
      }
    }

    return null
  }, [orderData, orderLoading])

  const onClickHaveOrderIcon = (orderId: string) => {
    getOrderData(orderId)
  }

  const columns = useMemo<ColumnsType>(() => {
    const columns: ColumnsType = [
      {
        title: '施設 / 時間',
        dataIndex: 'facilityAndTime',
        key: 'facilityAndTime',
        fixed: 'left',
        ellipsis: true,
      },
    ]

    timeArray.forEach((time, index) => {
      columns.push({
        title: time,
        key: `${index}${time}`,
        dataIndex: index,
        align: 'center',
        render: (orderId) =>
          orderId ? (
            <Popover
              key={`${index}${time}`}
              content={popoverContent}
              destroyTooltipOnHide={true}
              trigger="click"
            >
              <Button
                type="link"
                shape="circle"
                size="small"
                onClick={() => onClickHaveOrderIcon(orderId)}
                style={{
                  border: '0px',
                  padding: '0px',
                  backgroundColor: '#1890ff',
                  color: '#1890ff',
                }}
                icon={
                  <Icon.CloseCircleOutlined
                    style={{
                      color: '#1890ff',
                      fontSize: '18px',
                    }}
                  />
                }
              />
            </Popover>
          ) : (
            ''
          ),
      })
    })

    return columns
    // Reason for disable: if include onClickHaveOrderIcon as dep it will miss order, exm: order have 7 it will show 6
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popoverContent])

  const checkBookingTimes = useCallback((data: string[]) => {
    const formatData = []
    data?.forEach((item, index) => {
      if (item !== '') {
        formatData.push({ [index]: item })
      }
    })
    return formatData
  }, [])

  const formatVariation = useCallback(
    (productVariantData: BookingStatusOfTheDayProductVariants[]) => {
      const formatVariantData = []
      const variantHaveBooking = []

      productVariantData?.forEach((data) => {
        const getData = checkBookingTimes(data?.bookings)
        let variantData = {
          key: data?.id,
          facilityAndTime: data?.name,
        }

        getData?.forEach((obj) => {
          variantHaveBooking.push({ ...obj })
          return (variantData = { ...variantData, ...obj })
        })

        formatVariantData.push({
          ...variantData,
        })
      })

      return formatVariantData.length > 0
        ? {
            variantData: formatVariantData,
            variantHaveBooking: variantHaveBooking,
          }
        : null
    },
    [checkBookingTimes]
  )

  const dataSource = useMemo<ColumnsType>(() => {
    const dataForReturn = []
    data?.bookingStatusOfTheDay?.products?.forEach((product, index) => {
      if (product?.variants?.length > 1) {
        const getVariantHaveBooking = formatVariation(
          product?.variants
        )?.variantHaveBooking

        let formatVariationBookingData = {
          key: index,
          facilityAndTime: <strong>{product?.name}</strong>,
          children: formatVariation(product?.variants)?.variantData,
        }
        getVariantHaveBooking?.forEach((x) => {
          formatVariationBookingData = {
            ...formatVariationBookingData,
            ...x,
          }
        })

        dataForReturn.push({
          ...formatVariationBookingData,
        })
      } else {
        const getData = checkBookingTimes(product?.variants[0]?.bookings)
        let formatSingleVariationData = {
          key: index,
          facilityAndTime: <strong>{product?.name}</strong>,
        }
        getData?.forEach((obj) => {
          return (formatSingleVariationData = {
            ...formatSingleVariationData,
            ...obj,
          })
        })

        dataForReturn.push({
          ...formatSingleVariationData,
        })
      }
    })
    return dataForReturn
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <Table
      bordered
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      scroll={{ x: true }}
      loading={loading}
    />
  )
}
